import { Box, MenuItem, Select, TextField } from '@mui/material';
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Position, useEdges, useReactFlow } from 'reactflow';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import InputHandle from '../UI/InputHandle/InputHandle';
import Label from '../UI/Label/Label';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';

type CounterMode = 'up' | 'down' | 'wait';

type WhoIsChanging = {
  value: string | number;
  name: 'text' | 'waitSeconds' | '';
};

const ProcessCounter = memo(({ data, id, type }: NodeProps) => {
  const { setNodes: setNodeState, setEdges } = useReactFlow();

  const { setSchema, setSaving, flowId, onEdgeDelete } = useFlowsStore(state => state);
  const [counterMode, setCounterMode] = useState<CounterMode>('up');
  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [whoIsChanging, setWhoIsChanging] = useState<WhoIsChanging>({
    value: '',
    name: '',
  });
  const [hasToExecute, setHasToExecute] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [fixedNodeWidthPx, setFixedNodeWidthPx] = useState<number | null>(null);
  const debounceTimeoutRef = useRef<any>(null);
  const edges = useEdges();

  const onSubmit = (values: any) => {
    setIsLoading(true);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      changeType: 'execute',
      objectCallerData: [
        ...(user?.spaceObjects || []),
        ...(user?.objects || []),
      ],

      flowTriggerData: checkExecuteTrigger(data, id),
      flowId,
      objectData:
        counterMode === 'wait'
          ? {}
          : {
              text:
                counterMode === 'up'
                  ? Number(count) + 1
                  : Number(count) - 1,
            },
      setSchema,
    });

    setTimeout(() => {
      setHasToExecute(true);
    }, 1000);

    setIsLoading(false);
  };

  const onChange = (e: { target: { name: any; value: any } }) => {
    setCount(e.target.value);
    setWhoIsChanging({
      name: 'text',
      value: e.target.value,
    });
    setIsChangingDirectly(true);
  };

  const onChangeWait = (e: { target: { value: any } }) => {
    const value = Math.min(60, Math.max(0, Number(e.target.value)));
    setWaitSeconds(value);
    setWhoIsChanging({
      name: 'waitSeconds',
      value,
    });
    setIsChangingDirectly(true);
  };

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          changeType: 'changeText',
          flowId,
          objectData: {
            [whoIsChanging.name]: whoIsChanging.value,
          },
          setSchema,
        });

        setWhoIsChanging({
          value: '',
          name: '',
        });
      }
    },
    [isChangingDirectly, whoIsChanging],
    300,
  );

  const onExecuteNode = useMemo(() => {
    const values = {
      ...data,
    };

    setCount(data?.text ? parseInt(data?.text) : 0);

    if (data?.counterMode !== undefined) {
      setCounterMode(data.counterMode as CounterMode);
    } else if (data?.isIncreasing !== undefined) {
      setCounterMode(data.isIncreasing ? 'up' : 'down');
    }

    if (data?.waitSeconds !== undefined) {
      setWaitSeconds(data.waitSeconds);
    }

    return values;
  }, [data]);

  const onChangeMode = (mode: CounterMode) => {
    if (mode !== 'wait') {
      const edgesToRemove = edges.filter(
        edge => edge.target === id && edge.targetHandle === 'waitSecondsInput',
      );

      if (edgesToRemove.length > 0) {
        const edgesToRemoveIds = edgesToRemove.map(edge => edge.id);
        setEdges(eds => eds.filter(edge => !edgesToRemoveIds.includes(edge.id)));
        onEdgeDelete(edgesToRemove, edgesToRemove[0]?.id);
      }
    }

    setCounterMode(mode);
    nodeDataAutoSaveDynamic({
      setNodeState,
      id,
      setSaving,
      newEdges: edges,
      flowId,
      objectData: {
        counterMode: mode,
        isIncreasing: mode === 'up',
      },
      setSchema,
    });
  };

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        const effectiveWaitSeconds =
          data?.waitSecondsData?.length > 0
            ? Math.min(
                60,
                Math.max(0, Number(data.waitSecondsData[0]?.text) || 0),
              )
            : waitSeconds;

        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          const myValues = onExecuteNode;

          if (myValues) {
            onSubmit(myValues);
          }
        }, 1000 + effectiveWaitSeconds * 1000);
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [
    data?.flowTrigger,
    hasToExecute,
    onExecuteNode,
    data?.canceled,
    waitSeconds,
    data?.waitSecondsData,
  ]);

  useLayoutEffect(() => {
    if (fixedNodeWidthPx !== null) return;
    if (data?.collapsed) return;
    if (!rootRef.current) return;

    const width = Math.ceil(rootRef.current.getBoundingClientRect().width);
    if (Number.isFinite(width) && width > 0) {
      setFixedNodeWidthPx(width);
    }
  }, [data?.collapsed, fixedNodeWidthPx]);

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;
  const { user } = useAuthStore(state => state);
  const handleRun = (run: string) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      setSaving,
      id,
      flowId,
      objectData: {
        run,
      },
      setSchema,
    });
  };

  return (
    <Box className={isLoading ? 'node' : ''} sx={{ width: '320px' }}>
      <Node
        edges={edges}
        isGlobal
        isLoading={isLoading}
        handleRun={handleRun}
        type={type}
        id={id}
        data={data}
        showTokensUsed={false}
      >
        {!data?.collapsed && (
          <>
            <Box p={'16px'}>
              <Select
                value={counterMode}
                onChange={e => onChangeMode(e.target.value as CounterMode)}
                className="nodrag"
                sx={{
                  '& .MuiList-root': {
                    backgroundColor:
                      user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                  },
                  '& .MuiSvgIcon-root': {
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  },
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: 'none',
                  '.MuiOutlinedInput-notchedOutline': { border: 0 },
                  bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .Mui-selected': {
                        backgroundColor:
                          user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                      },
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    },
                  },
                }}
                name="openAiModels"
              >
                <MenuItem
                  value={'up'}
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                >
                  Count up
                </MenuItem>
                <MenuItem
                  value={'down'}
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                >
                  Count down
                </MenuItem>
                <MenuItem
                  value={'wait'}
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                >
                  Wait
                </MenuItem>
              </Select>
              {counterMode !== 'wait' && (
                <Box mt={'8px'}>
                  <TextField
                    className="nodrag"
                    type="number"
                    fullWidth
                    value={count}
                    name="count"
                    placeholder="Set starting number (optional)"
                    onChange={onChange}
                    InputProps={{
                      style: {
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      },
                    }}
                    sx={{
                      width: '100%',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      border: `2px solid ${
                        user?.theme === 'dark' ? '#475467' : '#EAECF0'
                      }`,
                    }}
                  />
                </Box>
              )}
              {counterMode === 'wait' && (
                <Box mt={'8px'}>
                  <Label
                    labelName="Seconds"
                    handleId="waitSecondsInput"
                    findColor={findColor}
                    disabled={isActiveEdge(edges, id, 'waitSecondsInput', 'target')}
                    isAdvanced
                  />
                  <TextField
                    className="nodrag"
                    fullWidth
                    type="number"
                    value={waitSeconds}
                    name="waitSeconds"
                    placeholder="0"
                    onChange={onChangeWait}
                    disabled={
                      (data?.waitSecondsData?.length ?? 0) > 0
                    }
                    InputProps={{
                      inputProps: { min: 0, max: 60 },
                      style: {
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      },
                    }}
                    sx={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      border: `2px solid ${
                        user?.theme === 'dark' ? '#475467' : '#EAECF0'
                      }`,
                    }}
                  />
                </Box>
              )}
            </Box>
          </>
        )}

        {counterMode !== 'wait' && (
          <InputHandle
            activeColor={findColor?.color}
            handleId={'textOutput'}
            isActive={isActiveEdge(edges, id, 'textOutput', 'source')}
            right={-8}
            top={'70%'}
            position={Position.Right}
            type="source"
          />
        )}
      </Node>
    </Box>
  );
});

export default memo(ProcessCounter);
